import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, firstValueFrom, Subject, timer } from 'rxjs';
import { catchError, concatMap, filter, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RestService } from '@cumlaude/shared-services';
import { toLocalDateTime } from '@cumlaude/shared-utils';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { BugsnagService } from '@cumlaude/bugsnag';
import { NotifiableError } from '@bugsnag/js';
import { UserService } from './user.service';
import {
	NavigatieFeedbackDialogComponent,
	NavigatieFeedbackResultaat,
} from '../dialogs/overig/navigatie-feedback-dialog/navigatie-feedback-dialog.component';

/**
 * Enige plek waar de default staat.
 */
const DEFAULT_NIEUWE_NAVIGATIE = true;

/**
 * Minimale tijd tussen twee keer terugschakelen naar de oude navigatie voordat we opnieuw de reden vragen.
 */
const MIN_TUSSENTIJD_FEEDBACK_MS = 30 * 60 * 1000;

/**
 * Hoe lang 'Vraag me later' de feedbackvraag uitstelt.
 */
const SNOOZE_DUUR_MS = 24 * 60 * 60 * 1000;

/**
 * Wachttijd na het laden van de app voordat een verlopen snooze de dialog opent, zodat de app eerst rustig kan laden.
 */
const START_VERTRAGING_MS = 3000;

/**
 * Wachttijd na het terugschakelen naar de oude navigatie voordat de dialog opent, zodat de gebruiker eerst de oude
 * navigatie ziet en de vraag niet meteen in beeld springt.
 */
const NA_SWITCH_VERTRAGING_MS = 2000;

/**
 * Of de gebruiker ooit bewust een keuze heeft gemaakt -- los van de effectieve stand. Alleen voor metrics.
 */
export type NavigatieKeuze = 'aan' | 'uit' | 'niet-gekozen';

@Injectable({
	providedIn: 'root',
})
export class NavigatieVoorkeurService {
	private readonly restService = inject(RestService);
	private readonly userService = inject(UserService);
	private readonly toastr = inject(ToastrService);
	private readonly bugsnag = inject(BugsnagService);
	private readonly dialog = inject(Dialog);
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);

	/**
	 * Lokale override na een (optimistische) toggle. undefined = nog geen lokale actie, volg het account.
	 */
	private readonly keuze = signal<boolean | undefined>(undefined);

	/**
	 * Lokale stand van de snooze, want het account wordt maar één keer geladen. undefined = nog geen lokale actie, volg
	 * het account; null = geen snooze.
	 */
	private readonly snoozeTotLokaal = signal<string | null | undefined>(undefined);

	private readonly snoozeTot = computed((): Date | null => {
		const lokaal = this.snoozeTotLokaal();
		const waarde = lokaal === undefined ? this.userService.account()?.navigatieFeedbackSnoozeTot : lokaal;
		return waarde ? new Date(waarde) : null;
	});

	/**
	 * Tijdstip van de laatste keer dat de gebruiker naar de oude navigatie schakelde. Bewust alleen in het geheugen.
	 */
	private laatsteSwitchNaarOud: number | undefined;

	private feedbackDialogOpen = false;

	/**
	 * Effectieve stand: wat de gebruiker feitelijk heeft. Dit stuurt de navigatielayout.
	 */
	readonly nieuweNavigatie = computed((): boolean | undefined => {
		const lokaleKeuze = this.keuze();
		if (lokaleKeuze !== undefined) return lokaleKeuze;

		const account = this.userService.account();
		if (!account) return undefined;

		return account.nieuweNavigatie ?? DEFAULT_NIEUWE_NAVIGATIE;
	});

	readonly navigatieKeuze = computed<NavigatieKeuze>(() => {
		const waarde = this.keuze() ?? this.userService.account()?.nieuweNavigatie;
		if (waarde === undefined) return 'niet-gekozen';
		return waarde ? 'aan' : 'uit';
	});

	/**
	 * concatMap serialiseert de PUT's: de vorige moet echt zijn afgerond (en zo nodig zijn
	 * teruggedraaid) voordat de volgende wordt verstuurd. switchMap annuleert alleen de
	 * client-subscriptie, niet de al onderweg zijnde HTTP-request, waardoor een tragere oudere
	 * request na een nieuwere kon committen.
	 */
	private readonly writes = new Subject<{ waarde: boolean; vorige: boolean | undefined }>();

	constructor() {
		this.writes
			.pipe(
				concatMap(({ waarde, vorige }) =>
					this.restService.putVoorkeuren({ nieuweNavigatie: waarde }).pipe(
						catchError((err) => {
							this.keuze.set(vorige);
							this.bugsnag.notify(<NotifiableError>err);
							this.toastr.error('Je voorkeur voor de nieuwe navigatie kon niet worden opgeslagen.');
							return EMPTY;
						})
					)
				),
				takeUntilDestroyed()
			)
			.subscribe();
	}

	setNieuweNavigatie(waarde: boolean) {
		const vorige = this.keuze();
		this.keuze.set(waarde);
		if (!waarde) this.laatsteSwitchNaarOud = Date.now();
		this.writes.next({ waarde, vorige });
	}

	/**
	 * Of we bij het terugschakelen naar de oude navigatie de reden moeten vragen. Een actieve snooze wint van de
	 * 30-minutenregel; zonder snooze vragen we het niet opnieuw als de gebruiker net al naar de oude navigatie schakelde.
	 * Roep dit aan vóór {@link setNieuweNavigatie}, want die legt het tijdstip van deze switch vast.
	 */
	moetFeedbackVragen(): boolean {
		const nu = Date.now();
		const snoozeTot = this.snoozeTot();
		if (snoozeTot && snoozeTot.getTime() > nu) return false;

		return this.laatsteSwitchNaarOud === undefined || nu - this.laatsteSwitchNaarOud >= MIN_TUSSENTIJD_FEEDBACK_MS;
	}

	/**
	 * Vraagt na het laden van de app alsnog de reden als de snooze verlopen is en de gebruiker de oude navigatie gebruikt.
	 */
	controleerBijStart() {
		this.userService.myAccount$
			.pipe(
				take(1),
				switchMap(() => this.router.events),
				filter((event) => event instanceof NavigationEnd),
				take(1),
				switchMap(() => timer(START_VERTRAGING_MS)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				const snoozeTot = this.snoozeTot();
				if (this.nieuweNavigatie() === false && snoozeTot && snoozeTot.getTime() <= Date.now()) this.vraagReden();
			});
	}

	/**
	 * Vraagt de reden na een korte vertraging, tenzij de gebruiker intussen weer naar de nieuwe navigatie is gegaan.
	 */
	vraagRedenNaSwitch() {
		timer(NA_SWITCH_VERTRAGING_MS)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (this.nieuweNavigatie() === false) this.vraagReden();
			});
	}

	/**
	 * Toont de dialog met de vraag waarom de gebruiker teruggaat naar de oude navigatie en verwerkt het antwoord.
	 */
	async vraagReden() {
		if (this.feedbackDialogOpen) return;
		this.feedbackDialogOpen = true;

		try {
			const dialogRef = this.dialog.open<NavigatieFeedbackResultaat>(NavigatieFeedbackDialogComponent, { disableClose: true });
			const resultaat = await firstValueFrom(dialogRef.closed);

			if (resultaat === 'later') this.setSnooze(new Date(Date.now() + SNOOZE_DUUR_MS));
			else if (this.snoozeTot()) this.setSnooze(null);
		} finally {
			this.feedbackDialogOpen = false;
		}
	}

	private setSnooze(tot: Date | null) {
		const snoozeTot = tot ? toLocalDateTime(tot) : null;
		this.snoozeTotLokaal.set(snoozeTot);
		this.restService
			.putNavigatieFeedbackSnooze({ snoozeTot })
			.pipe(
				catchError((err) => {
					this.bugsnag.notify(<NotifiableError>err);
					this.toastr.error('Je keuze voor het uitstellen van de feedback kon niet worden opgeslagen.');
					return EMPTY;
				})
			)
			.subscribe();
	}
}
